// ABOUTME: Claude API integration service for Oracle platform
// ABOUTME: Handles live API calls with timeout, retry, and fallback systems

import { convertOracleVariablesToMystical, validateOracleInputs } from '../constants/oracleMysticalMappings.js';
import ErrorHandler from './errorHandler.js';

class ClaudeAPIService {
  constructor() {
    this.directURL = 'https://api.anthropic.com/v1/messages';
    this.proxyURL = 'https://oraculo.buenaar.app/claude/api/claude';

    // MEMORY MANAGEMENT FIX: Implement proper cache with TTL and size limits
    this.contentCache = new Map();
    this.cacheTimestamps = new Map();
    this.maxCacheSize = 100; // Maximum number of cached items
    this.cacheTTL = 3600000; // 1 hour TTL in milliseconds

    this.timeoutMs = 30000;
    this.retryAttempts = 2;

    // Start cache cleanup interval
    this.startCacheCleanup();
  }

  // MEMORY MANAGEMENT FIX: Cache management methods
  startCacheCleanup() {
    // Clean up expired cache entries every 15 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredCache();
    }, 900000); // 15 minutes
  }

  cleanupExpiredCache() {
    const now = Date.now();
    let cleaned = 0;

    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp > this.cacheTTL) {
        this.contentCache.delete(key);
        this.cacheTimestamps.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`🧹 Cleaned up ${cleaned} expired cache entries`);
    }
  }

  evictOldestCacheEntry() {
    // Find oldest entry and remove it
    let oldestKey = null;
    let oldestTime = Date.now();

    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (timestamp < oldestTime) {
        oldestTime = timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.contentCache.delete(oldestKey);
      this.cacheTimestamps.delete(oldestKey);
      console.log(`🗑️ Evicted oldest cache entry: ${oldestKey}`);
    }
  }

  setCacheEntry(key, value) {
    // Clean up expired entries first
    this.cleanupExpiredCache();

    // If cache is full, evict oldest entry
    if (this.contentCache.size >= this.maxCacheSize) {
      this.evictOldestCacheEntry();
    }

    this.contentCache.set(key, value);
    this.cacheTimestamps.set(key, Date.now());
  }

  getCacheEntry(key) {
    // Check if entry exists and is not expired
    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) {
      return null;
    }

    if (Date.now() - timestamp > this.cacheTTL) {
      // Entry expired, remove it
      this.contentCache.delete(key);
      this.cacheTimestamps.delete(key);
      return null;
    }

    return this.contentCache.get(key);
  }

  // TD3-204: Strip markdown formatting from Claude API responses
  stripMarkdownFormatting(text) {
    if (!text) return text;

    return text
      // Remove markdown headers (# ## ###, etc.)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold (**text** or __text__)
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      // Remove italic (*text* or _text_)
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      // Remove bullet points (- or * at start of line)
      .replace(/^[\*\-]\s+/gm, '')
      // Trim excessive whitespace
      .trim();
  }

  convertToMysticalContext(variables, callType = null) {
    const validationErrors = validateOracleInputs(variables);

    const relevantErrors = validationErrors.filter(error => {
      if (callType === 'blockage' || callType === 'solution') {
        return !(error.includes('undefined') &&
                (error.includes('solution') || error.includes('diagnosis')));
      }
      return !error.includes('undefined');
    });

    if (relevantErrors.length > 0) {
      console.warn(`Oracle validation warnings for ${callType}:`, relevantErrors);
    }

    return convertOracleVariablesToMystical(variables);
  }

  buildPrompt(templateType, variables) {
    const mysticalVars = this.convertToMysticalContext(variables, templateType);
    
    const templates = {
      blockage: `Como um oráculo sábio e compassivo, analise o bloqueio principal baseado nas energias reveladas. Use sempre linguagem direta direcionada à pessoa ("você", "seu", "sua") em português brasileiro simples e acessível, com tom oracular mas acolhedor.

**ENERGIA PRIMÁRIA GUIA:**
- Raio escolhido: ${mysticalVars.rayName} - Representa a área da vida onde existe o bloqueio

**PERGUNTA DA CONSULENTE:**
"${mysticalVars.userQuestion}"

**CARTAS DE DIAGNÓSTICO REVELADAS:**
- Tipo de Carta: ${mysticalVars.diagnosisType} (aspectos de personalidade e padrões internos)
- Elemento: ${mysticalVars.diagnosisElement} (força natural que intensifica e amplifica o tipo acima)

**INSTRUÇÕES DE RESPOSTA:**
Gere um parágrafo de 80-100 palavras explicando apenas o bloqueio principal. Use as cartas de diagnóstico para revelar o bloqueio específico.

**DIRETRIZES DE INTEGRAÇÃO:**
- Foque no TIPO como o bloqueio principal e no ELEMENTO como intensificador
- Use o elemento para descrever a INTENSIDADE e QUALIDADE da manifestação do tipo
- Relacione tudo à pergunta específica da pessoa
- FOQUE APENAS NO DIAGNÓSTICO - não ofereça soluções ou caminhos
- SEMPRE dirija-se diretamente: "você", "seu", "sua" - NUNCA "essa pessoa" ou "a pessoa"
- NUNCA use primeira pessoa: evite "vejo", "percebo", "identifico" - use "há", "emerge", "revela-se"

**NEUTRALIDADE DE GÊNERO:**
- Quando inevitável, use sempre masculino: "consigo mesmo", "sozinho", etc.
- Evite adjetivos que marquem gênero - reformule para focar em sentimentos e processos
- Use linguagem mais suave: "não ser relevante" em vez de "ser insignificante"
- Prefira "gerar sensação de" e "aparentar" em vez de adjetivos pessoais

**TONE & STYLE:**
- Linguagem oracular, mas acessível
- Tom compassivo e sábio focado em revelar o bloqueio
- Use português brasileiro natural, evitando termos inventados ou muito duros`,

      solution: `Como um oráculo sábio e compassivo, analise a frequência necessária para equilibrar baseado nas energias reveladas. Use sempre linguagem direta direcionada à pessoa ("você", "seu", "sua") em português brasileiro simples e acessível, com tom oracular mas acolhedor.

**ENERGIA PRIMÁRIA GUIA:**
- Raio escolhido: ${mysticalVars.rayName} - Representa a área da vida onde o equilíbrio será restaurado

**PERGUNTA DA CONSULENTE:**
"${mysticalVars.userQuestion}"

**CARTAS DE SOLUÇÃO REVELADAS:**
- Tipo de Solução: ${mysticalVars.solutionType} (frequências transformadoras e potenciais a serem ativados)
- Elemento de Solução: ${mysticalVars.solutionElement} (força natural que amplifica e direciona a energia de desbloqueio)

**INSTRUÇÕES DE RESPOSTA:**
Gere um parágrafo de 80-100 palavras explicando apenas a frequência de equilíbrio necessária. Use as cartas de solução para revelar como essa energia específica criará harmonia.

**DIRETRIZES DE INTEGRAÇÃO:**
- Foque no TIPO como a frequência principal e no ELEMENTO como amplificador
- Use o elemento para descrever a INTENSIDADE e QUALIDADE da manifestação da solução
- Relacione tudo à pergunta específica da pessoa
- FOQUE APENAS NO EQUILÍBRIO - não mencione o bloqueio anterior
- SEMPRE dirija-se diretamente: "você", "seu", "sua" - NUNCA "essa pessoa" ou "a pessoa"
- NUNCA use primeira pessoa: evite "vejo", "percebo", "identifico" - use "há", "emerge", "revela-se"

**NEUTRALIDADE DE GÊNERO:**
- Quando inevitável, use sempre masculino: "consigo mesmo", "sozinho", etc.
- Evite adjetivos que marquem gênero - reformule para focar em sentimentos e processos
- Use linguagem equilibrada e esperançosa focada em potenciais
- Prefira "gerar sensação de" e "permitir que" para fluidez

**TONE & STYLE:**
- Linguagem oracular, mas acessível
- Tom esperançoso e empoderador focado em revelar o caminho
- Use português brasileiro natural, evitando termos inventados
- Foco em frequências e energias que trazem equilíbrio e desbloqueio`,

      wisdom: `Como um oráculo sábio e compassivo, compartilhe uma reflexão profunda que traga clareza interior. Use sempre linguagem direta direcionada à pessoa ("você", "seu", "sua") em português brasileiro simples e acessível, com tom oracular mas acolhedor.
Importante: Use linguagem neutra em relação a gênero sempre que possível. Quando não for possível manter neutralidade, utilize o masculino genérico.
Baseado nas energias reveladas:
- Raio escolhido: ${mysticalVars.rayName} - essência espiritual que permeia esta sabedoria
- Energia de Transformação: ${mysticalVars.solutionType} (caminho de evolução indicado)
- Elemento Amplificador: ${mysticalVars.solutionElement} (força natural que apoia a compreensão)

Gere uma reflexão filosófica de 50-80 palavras que ofereça perspectiva sábia sobre a jornada desta pessoa. Conecte essas energias em uma sabedoria universal que transcenda situações imediatas e traga compreensão mais profunda.

Use linguagem contemplativa focada em insights que expandem a consciência. Comece diretamente com a reflexão, sem títulos ou introduções formais.`,

      decree: `Como um oráculo sábio e compassivo, crie um decreto pessoal transformador. Use sempre linguagem direta e empoderada em português brasileiro simples e acessível.
Importante: Use linguagem neutra em relação a gênero sempre que possível. Quando não for possível manter neutralidade, utilize o masculino genérico.

Baseado nas energias reveladas:
- Raio escolhido: ${mysticalVars.rayName} - força primária que permeia o decreto
- Pergunta: "${mysticalVars.userQuestion}"
- Energia de Transformação: ${mysticalVars.solutionType} - caminho de evolução
- Elemento Amplificador: ${mysticalVars.solutionElement} - força natural de apoio

Crie um decreto pessoal de máximo 20 palavras. Deve ser uma afirmação positiva no presente ("Eu sou", "Eu manifesto", "Minha energia") que a pessoa possa repetir como mantra diário de transformação. Conecte harmoniosamente todas as energias reveladas.

Retorne apenas o decreto, sem aspas, títulos ou formatação extra.`
    };

    return templates[templateType] || '';
  }

  async shouldUseLiveAPI() {
    // SECURITY FIX: Never check for API keys in client-side code
    // API keys are now handled server-side only

    if (process.env.REACT_APP_USE_LIVE_API === 'true') {
      console.log('🚀 Claude API: Live API mode enabled - using server proxy');
      return true;
    }

    if (process.env.REACT_APP_ENV_MODE === 'production') {
      console.log('🚀 Claude API: Production mode - using server proxy');
      return true;
    }

    try {
      const proxyCheck = await fetch(`${this.proxyURL.replace('/claude/api/claude', '/claude/health')}`, { 
        method: 'GET',
        signal: AbortSignal.timeout(2000)
      });
      
      if (proxyCheck.ok) {
        console.log('🚀 Claude API: Proxy server available - using live API');
        return true;
      }
    } catch (error) {
      console.log('🔧 Claude API: Proxy server not available - using mock responses');
      console.log('💡 To use live API: run "npm run dev" or "npm run proxy" in a separate terminal');
    }
    
    return false;
  }

  async executeAPICall(callType, variables, sessionId, attempt = 1, userId = null) {
    const cacheKey = `${sessionId}-${callType}`;

    // MEMORY MANAGEMENT FIX: Use managed cache methods
    const cachedContent = this.getCacheEntry(cacheKey);
    if (cachedContent) {
      console.log(`🔄 Using cached content for ${callType}`);
      return cachedContent;
    }

    await this.saveSessionStateBeforeAPICall(sessionId, callType, variables);

    const prompt = this.buildPrompt(callType, variables);

    try {
      const useLiveAPI = await this.shouldUseLiveAPI();
      
      if (!useLiveAPI) {
        console.log(`🧪 Using mock response for ${callType} (development mode)`);
        const mockResponse = await this.getMockResponse(callType, variables);
        this.setCacheEntry(cacheKey, mockResponse);
        return mockResponse;
      }

      // SECURITY FIX: Always use proxy server, never direct API calls from client
      // This prevents API key exposure in client-side code
      const apiURL = this.proxyURL;
      const headers = {
        'Content-Type': 'application/json'
      };

      console.log(`🤖 Making live Claude API call for ${callType} via secure proxy (attempt ${attempt}/${this.retryAttempts + 1})`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.log(`⏱️ Claude API timeout after ${this.timeoutMs}ms for ${callType}`);
      }, this.timeoutMs);

      const response = await fetch(apiURL, {
        method: 'POST',
        headers: headers,
        signal: controller.signal,
        body: JSON.stringify({
          model: 'claude-sonnet-4-5-20250929',
          max_tokens: 4096,
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: prompt
                }
              ]
            }
          ],
          temperature: 0.7
          // REMOVED 2025-01-20: System prompt removed after stakeholder testing showed better results with user prompts only
          // Previous system: "Como um oráculo sábio e compassivo, você é especialista em análises místicas..."
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Claude API call failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      const rawContent = data.content[0].text;

      // TD3-204: Strip markdown formatting from API responses (# headers, **, etc.)
      const generatedContent = this.stripMarkdownFormatting(rawContent);

      console.log(`✅ Successfully generated ${callType} content (${generatedContent.length} chars)`);

      this.setCacheEntry(cacheKey, generatedContent);
      await this.clearSavedAPIState(sessionId, callType);

      return generatedContent;

    } catch (error) {
      console.error(`🚨 Oracle API call failed for ${callType} (attempt ${attempt}/${this.retryAttempts + 1}):`, error);

      // Capture error with appropriate classification
      let errorType = ErrorHandler.ERROR_TYPES.CLAUDE_API;
      let severity = 'warning'; // Default to warning, will escalate if needed

      if (error.name === 'AbortError') {
        console.error(`⏱️ Claude API timeout for ${callType} after ${this.timeoutMs}ms`);
        await ErrorHandler.captureError(
          errorType,
          `Claude API timeout for ${callType} after ${this.timeoutMs}ms`,
          userId,
          { sessionId, callType, attempt, timeout: this.timeoutMs }
        );

        if (attempt <= this.retryAttempts) {
          console.log(`🔄 Retrying ${callType} call (attempt ${attempt + 1}/${this.retryAttempts + 1})`);
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          return this.executeAPICall(callType, variables, sessionId, attempt + 1, userId);
        }

        return await this.handleAPITimeout(callType, variables, sessionId);
      }

      if (error.message.includes('401')) {
        console.error('🔑 Authentication failed - check your Claude API key');
        await ErrorHandler.captureError(
          errorType,
          'Claude API authentication failed - invalid API key',
          userId,
          { sessionId, callType, httpStatus: 401 }
        );
      } else if (error.message.includes('429')) {
        console.error('🚦 Rate limit exceeded - please wait before trying again');
        await ErrorHandler.captureError(
          errorType,
          'Claude API rate limit exceeded',
          userId,
          { sessionId, callType, httpStatus: 429, attempt }
        );

        if (attempt <= this.retryAttempts) {
          console.log(`🔄 Retrying ${callType} after rate limit (attempt ${attempt + 1}/${this.retryAttempts + 1})`);
          await new Promise(resolve => setTimeout(resolve, 5000 * attempt));
          return this.executeAPICall(callType, variables, sessionId, attempt + 1, userId);
        }
      } else if (error.message.includes('500')) {
        console.error('🛠️ Claude API server error - falling back to mock content');
        await ErrorHandler.captureError(
          errorType,
          'Claude API server error (500)',
          userId,
          { sessionId, callType, httpStatus: 500 }
        );
      } else {
        // Generic API error
        await ErrorHandler.captureError(
          errorType,
          `Claude API error: ${error.message}`,
          userId,
          { sessionId, callType, attempt, errorStack: error.stack }
        );
      }

      console.log(`🛡️ Using fallback content for ${callType}`);

      // TD3-203: CRITICAL alert - user received fallback content (Oracle broken)
      // This triggers Telegram alert via ErrorHandler since CLAUDE_API is CRITICAL severity
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.CLAUDE_API,
        `🚨 FALLBACK CONTENT SERVED: User received mock/fallback response for ${callType}. Claude API failed and user got content starting with "*". Oracle experience degraded - investigate immediately.`,
        userId,
        {
          sessionId,
          callType,
          attempt,
          fallbackIndicator: '*',
          userImpact: 'User received non-AI generated content',
          actionRequired: 'Check VPS Claude proxy logs and API status'
        }
      );

      const fallbackContent = this.getFallbackContent(callType, variables);
      this.setCacheEntry(cacheKey, fallbackContent);
      return fallbackContent;
    }
  }

  async executeParallelAPICalls(variables, sessionId, userId = null) {
    const wisdomVariables = {
      raySelection: variables.raySelection,
      solutionType: variables.solutionType,
      solutionElement: variables.solutionElement
    };

    const decreeVariables = {
      raySelection: variables.raySelection,
      userQuestion: variables.userQuestion,
      solutionType: variables.solutionType,
      solutionElement: variables.solutionElement
    };

    try {
      const [wisdomContent, decreeContent] = await Promise.all([
        this.executeAPICall('wisdom', wisdomVariables, sessionId, 1, userId),
        this.executeAPICall('decree', decreeVariables, sessionId, 1, userId)
      ]);

      return { wisdomContent, decreeContent };
    } catch (error) {
      await ErrorHandler.captureError(
        ErrorHandler.ERROR_TYPES.CLAUDE_API,
        `Parallel API calls failed: ${error.message}`,
        userId,
        { sessionId, variables, errorStack: error.stack }
      );
      throw error;
    }
  }

  async getMockResponse(callType, variables) {
    await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 1500));

    const mysticalVars = this.convertToMysticalContext(variables, callType);
    
    const rayParts = mysticalVars.rayName.split(' - ');
    const rayTheme = rayParts[0] || 'energia cósmica';
    const rayEnergy = rayParts[1] || 'vibração transformadora';
    
    const mockResponses = {
      blockage: `*O ${rayTheme} revela que seu principal bloqueio está relacionado à resistência em abraçar plenamente sua essência autêntica. Baseado em sua pergunta "${mysticalVars.userQuestion}", as energias de ${mysticalVars.diagnosisType} combinadas com ${mysticalVars.diagnosisElement} revelam padrões de autolimitação que impedem o florescimento natural de seu potencial. Este bloqueio se manifesta através de dúvidas internas que criam uma dissonância entre quem você é verdadeiramente e como se apresenta ao mundo.`,
      
      solution: `*Para transcender este bloqueio através do ${rayTheme}, as energias de ${mysticalVars.solutionType} com ${mysticalVars.solutionElement} indicam um caminho de transformação consciente. A chave está em reconectar-se com sua ${rayEnergy} através de práticas diárias que honrem essa vibração. Comece com pequenos rituais que fortaleçam sua conexão interior, permitindo que essa energia se expresse naturalmente em suas ações. Esta combinação energética sugere que a natureza será sua grande aliada neste processo de reconexão e cura.`,
      
      wisdom: `*A sabedoria do ${rayTheme}, combinada com as energias de ${mysticalVars.solutionType} e ${mysticalVars.solutionElement}, nos ensina que toda jornada de crescimento requer paciência e compaixão consigo mesmo. Como as estações da natureza, cada fase de evolução tem seu tempo sagrado. Sua ${rayEnergy} está despertando gradualmente, como um rio que encontra seu curso natural. Confie que cada experiência, mesmo as aparentemente desafiadoras, está moldando você para expressar mais plenamente sua essência autêntica.`,
      
      decree: `*${this.generateContextualDecree(mysticalVars, { theme: rayTheme, energy: rayEnergy }).replace(/^"|"$/g, '')}`
    };

    return mockResponses[callType] || 'Conteúdo oracular não disponível.';
  }

  generateContextualDecree(mysticalVars, rayContext) {
    const decreeTemplates = [
      `"Eu honro minha ${rayContext.energy} e manifesto ${rayContext.theme} em tudo que faço."`,
      `"Minha jornada flui com naturalidade e sabedoria através do ${rayContext.theme}."`,
      `"Eu sou um canal puro de ${rayContext.theme}, expressando minha verdade interior."`,
      `"Através do ${rayContext.theme}, encontro minha força e propósito únicos."`,
      `"Permito que minha ${rayContext.energy} guie cada passo de minha evolução."`
    ];
    
    const rayNumber = mysticalVars.rayName ? parseInt(mysticalVars.rayName.match(/\d+/)?.[0]) || 1 : 1;
    const index = (rayNumber - 1) % decreeTemplates.length;
    return decreeTemplates[index];
  }

  getFallbackContent(callType, variables) {
    const mysticalVars = this.convertToMysticalContext(variables, callType);
    const rayTheme = mysticalVars.rayName?.split(' - ')[0] || 'energia cósmica';
    
    const fallbacks = {
      blockage: `*O oráculo percebe energias em conflito que precisam de atenção. O ${rayTheme} sugere que questões internas estão criando resistências em sua jornada atual. Reflita sobre padrões que podem estar limitando seu crescimento.`,
      
      solution: `*As cartas indicam que a solução está dentro de você. O ${rayTheme} oferece força para mudanças necessárias. Busque equilíbrio entre ação e contemplação, permitindo que sua intuição guie os próximos passos.`,
      
      wisdom: `*A sabedoria ancestral nos lembra que todo desafio carrega em si a semente da transformação. Confie no processo e permita que cada experiência contribua para seu crescimento interior.`,
      
      decree: `*Eu confio na minha jornada e abraço cada passo do meu crescimento.`
    };

    return fallbacks[callType] || 'O oráculo oferece sua bênção para sua jornada.';
  }

  clearSessionCache(sessionId) {
    // MEMORY MANAGEMENT FIX: Clear both cache and timestamps
    const keysToDelete = Array.from(this.contentCache.keys()).filter(key =>
      key.startsWith(`${sessionId}-`)
    );
    keysToDelete.forEach(key => {
      this.contentCache.delete(key);
      this.cacheTimestamps.delete(key);
    });
    console.log(`🧹 Cleared cache for session ${sessionId} (${keysToDelete.length} items)`);
  }

  // MEMORY MANAGEMENT FIX: Add service cleanup method
  cleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.contentCache.clear();
    this.cacheTimestamps.clear();
    console.log('🧹 Claude API service cleaned up');
  }

  getAPIStatus() {
    // SECURITY FIX: Never check for API keys in client-side code
    const envMode = process.env.REACT_APP_ENV_MODE || 'development';
    const useLiveAPI = process.env.REACT_APP_USE_LIVE_API === 'true';

    return {
      serverSideAPI: true, // API keys handled securely on server
      envMode,
      useLiveAPI,
      cacheSize: this.contentCache.size,
      status: useLiveAPI ? '🟢 Live Claude API (Secure Proxy)' : '🟡 Mock responses'
    };
  }

  async testConnection() {
    // SECURITY FIX: Test connection through secure proxy only
    if (!this.shouldUseLiveAPI()) {
      return { success: false, reason: 'Live API not enabled' };
    }

    try {
      const testResponse = await fetch(`${this.proxyURL}/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (testResponse.ok) {
        return { success: true, message: 'Claude API proxy connection successful' };
      } else {
        return { success: false, reason: `Proxy returned ${testResponse.status}: ${testResponse.statusText}` };
      }
    } catch (error) {
      return { success: false, reason: error.message };
    }
  }

  async saveSessionStateBeforeAPICall(sessionId, callType, variables) {
    const stateKey = `oracle_api_state_${sessionId}_${callType}`;
    const apiState = {
      sessionId,
      callType,
      variables,
      timestamp: Date.now(),
      status: 'pending'
    };
    
    try {
      localStorage.setItem(stateKey, JSON.stringify(apiState));
      console.log(`💾 Saved API state for ${callType} before call`);
    } catch (error) {
      console.warn('Failed to save API state:', error);
    }
  }

  async clearSavedAPIState(sessionId, callType) {
    const stateKey = `oracle_api_state_${sessionId}_${callType}`;
    try {
      localStorage.removeItem(stateKey);
      console.log(`🗑️ Cleared saved API state for ${callType}`);
    } catch (error) {
      console.warn('Failed to clear API state:', error);
    }
  }

  async handleAPITimeout(callType, variables, sessionId) {
    console.log(`⏱️ Final timeout for ${callType} - offering user choice`);
    
    const timeoutFallback = this.getFallbackContent(callType, variables);
    const timeoutContent = timeoutFallback.replace('*', '*[Timeout] ');

    this.setCacheEntry(`${sessionId}-${callType}`, timeoutContent);
    return timeoutContent;
  }

  async recoverSavedAPIStates() {
    const apiStates = [];
    const keys = Object.keys(localStorage).filter(key => key.startsWith('oracle_api_state_'));
    
    for (const key of keys) {
      try {
        const stateData = JSON.parse(localStorage.getItem(key));
        const isStale = Date.now() - stateData.timestamp > 300000;
        
        if (isStale) {
          localStorage.removeItem(key);
          console.log(`🧹 Removed stale API state: ${key}`);
        } else {
          apiStates.push(stateData);
        }
      } catch (error) {
        localStorage.removeItem(key);
        console.warn(`🗑️ Removed corrupted API state: ${key}`);
      }
    }
    
    return apiStates;
  }
}

const claudeAPIService = new ClaudeAPIService();
export default claudeAPIService;